import { motion } from "framer-motion";
import { TrashIcon } from "lucide-react";

import { Button } from "../../Button";

import { TOOL_DEPENDENCY_FORM_ROWS } from "./field-config";
import {
  OpenaiYamlToolAccordionContent,
  OpenaiYamlToolAccordionItem,
  OpenaiYamlToolAccordionRoot,
  OpenaiYamlToolAccordionTrigger,
} from "./ToolAccordionPrimitives";
import { OpenaiYamlToolDependencyFormFieldCell } from "./ToolDependencyFormFieldCell";
import type { ToolDependency } from "./types";

export interface OpenaiYamlToolCardProps {
  tool: ToolDependency;
  index: number;
  readOnly?: boolean;
  onRemove: (index: number) => void;
  onUpdate: (index: number, patch: Partial<ToolDependency>) => void;
}

export const OpenaiYamlToolCard = ({
  tool,
  index,
  readOnly = false,
  onRemove,
  onUpdate,
}: OpenaiYamlToolCardProps) => {
  const panelId = `tool-dep-${index}`;
  const summary =
    tool.value.trim() || tool.url.trim() || tool.description.trim() || "Configure MCP dependency";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2 }}
      className="overflow-hidden rounded-md"
    >
      <OpenaiYamlToolAccordionRoot type="single" collapsible defaultValue={panelId}>
        <OpenaiYamlToolAccordionItem
          value={panelId}
          className="rounded-md border border-gray-200 bg-gray-50/50 data-[state=open]:border-gray-200"
        >
          <div className="flex w-full flex-col [&>h3]:w-full">
            <OpenaiYamlToolAccordionTrigger
              className="w-full flex-1 py-2 pl-3 pr-2 text-left text-xs font-medium text-gray-700 hover:bg-gray-100/80 hover:no-underline [&[data-state=open]]:border-b [&[data-state=open]]:border-gray-200/80"
              chevronClassName="self-center"
            >
              <div className="flex w-full items-center justify-between">
                <span className="flex min-w-0 flex-col items-start gap-0.5 pr-1">
                  <span className="text-gray-600">Tool {index + 1}</span>
                  <span className="max-w-full truncate text-[11px] font-normal leading-tight text-gray-500">
                    {summary}
                  </span>
                </span>
                {!readOnly ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="tinyIcon"
                    className="mr-2 shrink-0 self-center rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-500"
                    aria-label={`Remove tool ${index + 1}`}
                    onClick={e => {
                      e.preventDefault();
                      onRemove(index);
                    }}
                  >
                    <TrashIcon className="h-3.5 w-3.5" />
                  </Button>
                ) : null}
              </div>
            </OpenaiYamlToolAccordionTrigger>
          </div>
          <OpenaiYamlToolAccordionContent>
            <div className="space-y-2 border-t border-gray-200/80 px-3 pt-3">
              {TOOL_DEPENDENCY_FORM_ROWS.map(row => (
                <div
                  key={row.fields.map(f => f.key).join("-")}
                  className={row.columns === 2 ? "grid grid-cols-2 gap-2" : undefined}
                >
                  {row.fields.map(field => (
                    <OpenaiYamlToolDependencyFormFieldCell
                      key={field.key}
                      field={field}
                      panelId={panelId}
                      tool={tool}
                      index={index}
                      readOnly={readOnly}
                      onUpdate={onUpdate}
                    />
                  ))}
                </div>
              ))}
            </div>
          </OpenaiYamlToolAccordionContent>
        </OpenaiYamlToolAccordionItem>
      </OpenaiYamlToolAccordionRoot>
    </motion.div>
  );
};
